/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StyleSheet, View, TouchableOpacity } from 'react-native';
import { isWeb } from '@global/screens';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';
import * as Layout from '@components/screen/Layout';
import TableTargetSetting from '@/screens/setting/target-setting/components/TableTargetSetting';

import {
  MONTH_NUMBERS,
  MONTH_NUMBERS_FULLNAME,
  MONTH_KEYS,
  MONTH_TABLES,
  MONTH_TABLES_WITHOUT_TOTAL,
  IGNORE_TARGET_ID,
} from '@/constants/target-settings';
import { SALESMAN_TYPE_OBJ } from '@/constants';

import useApi from '@/hooks/useApi';
import { MsB2b } from '@api/ms-b2b';
import { MsAccount } from '@api/ms-account';
import { formatTargetSettingTitle } from '@global/helpers';
import { showAlert } from '@/store/global';

const TABS = [
  {
    label: 'MT',
    value: 'mt',
  },
  {
    label: 'GT',
    value: 'gt',
  },
  {
    label: 'Overall',
    value: 'overall',
  },
];

const TargetSettingApprovalScreen = (props) => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.auth.user);
  const userRole = userData?.role?.code;
  const [tableData, setTableData] = useState([]);
  const [detailData, setDetailData] = useState({});
  const [tableStructures, setTableStructures] = useState([]);
  const [tableHeader, setTableHeader] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('national');
  const [isShowTab, setIsShowTab] = useState(false);
  const [monthName, setMonthName] = useState('');

  const paramType = props?.route?.params?.type;
  const paramTargetType = props?.route?.params?.targetType;
  const targetYear = props?.route?.params?.targetYear;
  const targetId = props?.route?.params?.targetId;
  const isSalesValue = Boolean(paramType == 'sales-value');
  const isDoors = Boolean(paramType == 'doors');
  const isProductFocus = Boolean(paramType == 'product-focus');
  const isSalesValueBranch =
    isSalesValue && ['branch-gt', 'branch-mt'].includes(paramTargetType);
  const isSalesValueNational = isSalesValue && paramTargetType == 'national';
  const isSalesValueChannel = isSalesValue && paramTargetType == 'channel';
  const isSalesValueSalesman = isSalesValue && paramTargetType == 'salesman';
  const isDoorsSalesman = isDoors && paramTargetType == 'salesman';

  const getAllTargetSettingByLevel = useApi(MsB2b.getAllTargetSettingByLevel);
  const getAllBranches = useApi(MsAccount.getAllBranches);

  const titlePage = useMemo(() => {
    return props?.route?.params?.title;
  }, [props]);

  const formattedTitleForMessage = useMemo(() => {
    return formatTargetSettingTitle({
      titlePage,
      paramType,
      paramTargetType,
      titleSection: props?.route?.params?.titleSection || '',
    });
  }, [
    titlePage,
    paramType,
    paramTargetType,
    props?.route?.params?.titleSection,
  ]);

  const targetSettingType = useMemo(() => {
    const targetTypeKey = paramTargetType.replace('-gt', '').replace('-mt', '');
    return `${paramType}-${targetTypeKey}`;
  }, [paramType, paramTargetType]);

  const customSalesTargetHeader = useMemo(() => {
    return isSalesValueBranch || isSalesValueSalesman
      ? {
          body: 'sales_target',
          label: `Sales Target ${props?.route?.params?.targetYear}`,
        }
      : ``;
  }, [monthName, props?.route?.params?.targetYear]);

  const showApproval = useMemo(() => {
    return (
      detailData?.current_status == 'need_approval' &&
      detailData?.current_approval == userRole
    );
  }, [detailData]);

  const convertValue = (data = []) => {
    let result = [];

    if (isProductFocus) {
      if (data?.length) {
        const unique_array = [];

        for (let i = 0; i < data.length; i++) {
          const item = data[i];
          let sub_level = '';
          if (paramTargetType?.indexOf('gt') > -1) {
            sub_level = 'gt';
          } else if (paramTargetType?.indexOf('mt') > -1) {
            sub_level = 'mt';
          }
          if (
            item.salesman_type &&
            unique_array.findIndex((s) => s.code == item.salesman_type) == -1 &&
            item.sub_level == sub_level
          ) {
            const obj = {
              name: SALESMAN_TYPE_OBJ[item.salesman_type],
              code: item.salesman_type,
              data: [],
              sub_level,
            };
            for (let j = 1; j <= 3; j++) {
              const obj_brand = {
                salesman_type: `Brand Focus ${j}`,
              };
              const obj_sku = {
                salesman_type: 'SKU',
              };
              const obj_target = {
                salesman_type: 'Target',
                data: {},
                detail: obj.detail,
              };
              const obj_empty = {
                salesman_type: '--',
              };

              for (let k = 0; k < MONTH_KEYS.length; k++) {
                const month = MONTH_KEYS[k];
                const findData = data.find(
                  (a) =>
                    a.month == k + 1 &&
                    a.salesman_type == obj.code &&
                    obj.sub_level == sub_level,
                );

                obj_brand[month] =
                  findData &&
                  findData[`brand_id_focus_${j}`] &&
                  findData[`brand_id_focus_${j}`]?.name
                    ? findData[`brand_id_focus_${j}`]['name']
                    : '-';
                obj_brand['total'] = '-';

                obj_sku[month] =
                  findData && findData[`sku_${j}`] ? findData[`sku_${j}`] : '-';
                obj_sku['total'] = '-';

                obj_target[month] =
                  findData && findData[`value_${j}`]
                    ? findData[`value_${j}`]
                    : 0;
                obj_target['data'][month] = findData;
                obj_target['total'] =
                  (obj_target['total'] || 0) + obj_target[month];

                obj_empty[month] = '--';
                obj_empty['total'] = '--';
              }

              obj.data.push(obj_brand);
              obj.data.push(obj_sku);
              obj.data.push(obj_target);
              obj.data.push(obj_empty);
            }
            unique_array.push(obj);
          }
        }

        result = unique_array;
      }
    } else {
      const brands = [];
      const isOverallChannel = isSalesValueChannel && activeTab == 'overall';

      for (let i = 0; i < data.length; i++) {
        const item = data[i];
        if (item?.salesman_type) {
          item.salesman_type = item.salesman_type.replace('\n', '');
        }
        const obj = {
          id: item?._id,
          object_id: isSalesValueSalesman ? item?._id : item?.brand?.id,
          sales_code: isSalesValueSalesman ? item.sales_code : '',
          brand: item?.brand?.name,
          salesman_code: item?.sales_code,
          salesman_type:
            item?.salesman_type && SALESMAN_TYPE_OBJ[item.salesman_type]
              ? SALESMAN_TYPE_OBJ[item.salesman_type]
              : '',
          sku: item?.sku,
          total: 0,
        };
        if (isSalesValueChannel) {
          obj['market_type'] = item?.market_type?.code;
        }
        const hasNoSameBrand =
          brands.findIndex((b) => {
            if (isSalesValueSalesman) {
              return b.sales_code == obj.sales_code;
            }
            return (
              b.object_id == obj.object_id &&
              (isDoors ? b.sku == obj.sku : true)
            );
          }) == -1;
        if (hasNoSameBrand || isOverallChannel) {
          brands.push(obj);
        }
      }

      if (brands?.length) {
        const total_target = {
          brand: 'Total',
          ...(isDoorsSalesman || isSalesValueSalesman
            ? {
                salesman_code: 'Total',
              }
            : {}),
        };
        for (let i = 0; i < brands.length; i++) {
          const item = brands[i];
          const obj = JSON.parse(JSON.stringify(item));
          const check = data
            .filter((b) => {
              if (isSalesValueSalesman) {
                return b?.sales_code == item.sales_code;
              } else if (isDoors) {
                return b?.brand?.id == item.object_id && b.sku == item.sku;
              } else {
                return b?.brand?.id == item.object_id;
              }
            })
            .sort((a, b) => {
              return a?.month - b?.month;
            });

          obj['data'] = {};
          obj['split'] = {};
          for (let j = 1; j <= 12; j++) {
            const child = check?.find((c) => c?.month == j);
            const split_value = {
              mt: 0,
              gt: 0,
            };
            if (isOverallChannel) {
              const checkValueMt = check?.find(
                (c) => c?.month == j && c?.market_type?.code == 'mt',
              );
              if (checkValueMt) {
                split_value['mt'] =
                  checkValueMt?.target_value &&
                  !isNaN(Number(checkValueMt.target_value))
                    ? Number(checkValueMt.target_value)
                    : 0;
              }
              const checkValueGt = check?.find(
                (c) => c?.month == j && c?.market_type?.code == 'gt',
              );
              if (checkValueGt) {
                split_value['gt'] =
                  checkValueGt?.target_value &&
                  !isNaN(Number(checkValueGt.target_value))
                    ? Number(checkValueGt.target_value)
                    : 0;
              }
            }
            obj['split'][MONTH_NUMBERS[j]] = split_value;
            if (child?.target_value && !isNaN(Number(child.target_value))) {
              if (isOverallChannel) {
                obj[MONTH_NUMBERS[j]] = Number(child.target_value);
                obj['data'][MONTH_NUMBERS[j]] = child || null;
              } else {
                obj[MONTH_NUMBERS[j]] = Number(child.target_value);
                obj['data'][MONTH_NUMBERS[j]] = child || null;
              }
            } else {
              obj[MONTH_NUMBERS[j]] = 0;
              obj['data'][MONTH_NUMBERS[j]] = child || null;
            }

            const obj_value = obj[MONTH_NUMBERS[j]];
            obj['total'] = (obj['total'] || 0) + obj_value;
            // isSalesValueBranch || isSalesValueSalesman
            obj['sales_target'] = (obj['sales_target'] || 0) + obj_value;

            total_target[MONTH_NUMBERS[j]] =
              (total_target[MONTH_NUMBERS[j]] || 0) + obj_value;
            total_target['total'] = (total_target['total'] || 0) + obj_value;
            total_target['sales_target'] =
              (total_target['sales_target'] || 0) + obj_value;
          }
          result.push(obj);
        }
        if (
          (isSalesValueChannel || isSalesValueSalesman || isSalesValueBranch) &&
          activeTab != 'overall'
        ) {
          result.push(total_target);
        }
      }
    }

    return result;
  };

  const doSearch = async () => {
    setDetailData({});
    setMonthName('');
    setLoading(true);
    try {
      const type = props?.route?.params?.type;
      const targetType = props?.route?.params?.targetType
        .replace('-gt', '')
        .replace('-mt', '');
      let response = null;
      const filter = {
        year: targetYear,
      };
      if (isSalesValueBranch || isProductFocus) {
        if (paramTargetType?.indexOf('gt') > -1) {
          filter['type'] = 'gt';
        } else if (paramTargetType?.indexOf('mt') > -1) {
          filter['type'] = 'mt';
        }
      }
      if (isSalesValueChannel) {
        if (['mt', 'gt', 'national'].includes(activeTab)) {
          const marketCode = activeTab == 'national' ? 'mt' : activeTab;
          filter['market_type.code'] = marketCode;
        }
      }
      if (targetId && !IGNORE_TARGET_ID.includes(targetId)) {
        filter['team_id'] = targetId;
      }
      if (isSalesValueBranch) {
        response = await getAllBranches.request();
      } else {
        response = await getAllTargetSettingByLevel.request(type, targetType, {
          params: {
            filter,
          },
        });
      }
      if (response?.data?.data?.length) {
        const res = response?.data?.data;
        if (!isSalesValueBranch) {
          setDetailData(res[0]);
        }
        let result = [];
        const overall_result = [];
        if (isSalesValueBranch) {
          const child_response = await getAllTargetSettingByLevel.request(
            type,
            targetType,
            {
              params: {
                filter,
              },
            },
          );
          const temps = child_response?.data?.data?.length
            ? child_response.data.data
            : [];
          if (isSalesValueBranch && temps?.length) {
            setDetailData(temps[0]);
          }
          const code = props?.route?.params?.targetType?.replace('branch-', '');
          if (
            temps?.length &&
            Boolean(temps.find((a) => a?.market_type?.code == code)) &&
            temps?.[0]?.month
          ) {
            setMonthName(MONTH_NUMBERS_FULLNAME[temps?.[0]?.month]);
          }
          const total_target = {
            branch: 'Total',
          };
          for (let i = 0; i < res.length; i++) {
            const item = res[i];
            const selected = temps.filter((b) => b?.branch?.id == item?._id);
            const obj = {
              id: item?._id,
              branch: item?.name,
              data: {},
              // sales_target:
              //   selected?.target_value && !isNaN(Number(selected.target_value))
              //     ? Number(selected.target_value)
              //     : 0,
            };
            for (let j = 1; j <= 12; j++) {
              const month_child = selected?.find((c) => c?.month == j);
              obj[MONTH_NUMBERS[j]] =
                month_child?.target_value &&
                !isNaN(Number(month_child.target_value))
                  ? Number(month_child.target_value)
                  : 0;

              const obj_value = obj[MONTH_NUMBERS[j]];
              obj['total'] = (obj['total'] || 0) + obj_value;

              total_target[MONTH_NUMBERS[j]] =
                (total_target[MONTH_NUMBERS[j]] || 0) + obj_value;
              total_target['total'] = (total_target['total'] || 0) + obj_value;
            }
            result.push(obj);
          }
          result.push(total_target);
        } else {
          if (res?.[0]?.month) {
            setMonthName(MONTH_NUMBERS_FULLNAME[res?.[0]?.month]);
          }
          result = convertValue(res);
          if (isSalesValueChannel && result?.length) {
            // overall_result
            const obj_overall = {
              mt: {
                brand: 'Total MT',
              },
              gt: {
                brand: 'Total GT',
              },
              total: {
                brand: 'Total Target',
              },
            };
            const done = [];
            for (let i = 0; i < result.length; i++) {
              const item = result[i];
              if (
                done.findIndex(
                  (a) =>
                    a.name == item?.brand && a.market_type == item?.market_type,
                ) == -1
              ) {
                done.push({
                  name: item?.brand,
                  market_type: item?.market_type,
                });
                for (let j = 0; j < MONTH_KEYS.length; j++) {
                  const month = MONTH_KEYS[j];
                  const total_value =
                    item['split']?.[month]?.[item?.market_type] || 0;

                  if (item?.market_type == 'mt') {
                    obj_overall['mt'][month] =
                      (obj_overall['mt'][month] || 0) + total_value;
                    obj_overall['mt']['total'] =
                      (obj_overall['mt']['total'] || 0) + total_value;
                  } else if (item?.market_type == 'gt') {
                    obj_overall['gt'][month] =
                      (obj_overall['gt'][month] || 0) + total_value;
                    obj_overall['gt']['total'] =
                      (obj_overall['gt']['total'] || 0) + total_value;
                  } else {
                    // obj_overall['gt'][month] = obj_overall['gt'][month] || 0;
                    // obj_overall['gt']['total'] = obj_overall['gt']['total'] || 0;
                    // obj_overall['mt'][month] = obj_overall['mt'][month] || 0;
                    // obj_overall['mt']['total'] = obj_overall['mt']['total'] || 0;
                  }

                  obj_overall['total'][month] =
                    (obj_overall['mt'][month] || 0) +
                    (obj_overall['gt'][month] || 0);
                  obj_overall['total']['total'] =
                    (obj_overall['mt']['total'] || 0) +
                    (obj_overall['gt']['total'] || 0);
                }
              }
            }
            overall_result.push(obj_overall.mt);
            overall_result.push(obj_overall.gt);
            overall_result.push(obj_overall.total);
          }
        }

        if (isSalesValueChannel) {
          if (activeTab == 'overall') {
            setTableData(overall_result);
          } else {
            setTableData(result);
          }
        } else {
          if (isSalesValueNational || isDoors) {
            if (result?.length) {
              const obj = {
                brand: 'Total',
              };
              const keys = [...MONTH_KEYS, 'total'];
              for (let i = 0; i < result.length; i++) {
                const item = result[i];
                for (let j = 0; j < keys.length; j++) {
                  const child = keys[j];
                  const total = obj[child] || 0;
                  const child_total = item[child] || 0;
                  obj[child] = total + child_total;
                }
              }
              result.push(obj);
            }
          }
          setTableData(result);
        }
      } else {
        setTableData([]);
      }
      setLoading(false);
    } catch (err) {
      console.log('err', err);
      setLoading(false);
    }
  };

  const onSubmit = async (status = 'rejected') => {
    try {
      // const targetLevel = props?.route?.params?.targetLevel;
      const targetSubLevel = props?.route?.params?.targetSubLevel;
      const payload = {
        year: targetYear,
      };
      // if (targetLevel) {
      //   payload['level'] = targetLevel;
      // }
      if (targetSubLevel) {
        payload['type'] = targetSubLevel;
      }
      // if (isSalesValueBranch || isProductFocus) {
      //   if (paramTargetType?.indexOf('mt') > -1) {
      //     payload['type'] = 'mt';
      //   } else if (paramTargetType?.indexOf('gt') > -1) {
      //     payload['type'] = 'gt';
      //   }
      // }
      const targetType = paramTargetType.replace('-gt', '').replace('-mt', '');
      if (targetId && !IGNORE_TARGET_ID.includes(targetId)) {
        payload['team_id'] = targetId;
      }
      await MsB2b.updateStatusTargetSetting(
        paramType,
        targetType,
        status,
        payload,
      );
      dispatch(
        showAlert({
          show: true,
          isConfirm: false,
          title:
            status == 'approved'
              ? `${formattedTitleForMessage} target Approved`
              : `${formattedTitleForMessage} target Rejected`,
          description:
            status == 'approved'
              ? `${formattedTitleForMessage} target has been approved.`
              : `${formattedTitleForMessage} target has been rejected.`,
          onCallback: () => {
            doSearch();
          },
        }),
      );
    } catch (error) {
      dispatch(
        showAlert({
          show: true,
          isConfirm: false,
          title: 'Error',
          description:
            error?.response?.data?.message || 'A system error occurred.',
        }),
      );
    }
  };

  const onChangeTab = () => {
    const targetYearTable = props?.route?.params?.targetYear;
    const DEFAULT_STRUCTURE = [
      [
        { label: 'Brand', body: 'brand', rowSpan: 2 },
        {
          label: `Sales Target ${targetYearTable}`,
          body: 'sales_target',
          colSpan: MONTH_TABLES.length,
        },
      ],
      MONTH_TABLES,
    ];

    const TABLE_HEADER_STRUCTURES = {
      'sales-value': {
        national: DEFAULT_STRUCTURE,
        channel: {
          mt: DEFAULT_STRUCTURE,
          gt: DEFAULT_STRUCTURE,
          overall: DEFAULT_STRUCTURE,
        },
        branch: [
          [
            { label: 'Branch', body: 'branch', rowSpan: 2 },
            {
              label: `Sales Target ${targetYearTable}`,
              body: 'sales_target',
              colSpan: MONTH_TABLES.length,
            },
          ],
          MONTH_TABLES,
        ],
        salesman: [
          [
            { label: 'Salesman Code', body: 'salesman_code', rowSpan: 2 },
            { label: 'Salesman Type', body: 'salesman_type', rowSpan: 2 },
            {
              label: `Sales Target ${targetYearTable}`,
              body: 'sales_target',
              colSpan: MONTH_TABLES.length,
            },
          ],
          MONTH_TABLES,
        ],
      },
      'product-focus': {
        salesman: [
          [
            { label: 'Sales Type', body: 'salesman_type', rowSpan: 2 },
            {
              label: `Sales Target ${targetYearTable}`,
              body: 'sales_target',
              colSpan: MONTH_TABLES_WITHOUT_TOTAL.length,
            },
          ],
          MONTH_TABLES_WITHOUT_TOTAL,
        ],
      },
      doors: {
        national: [
          [
            { label: 'Brand', body: 'brand', rowSpan: 2 },
            { label: 'SKU Reference', body: 'sku', rowSpan: 2 },
            {
              label: `Sales Target ${targetYearTable}`,
              body: 'sales_target',
              colSpan: MONTH_TABLES.length,
            },
          ],
          MONTH_TABLES,
        ],
        salesman: [
          [
            { label: 'Salesman Code', body: 'salesman_code', rowSpan: 2 },
            { label: 'Salesman Type', body: 'salesman_type', rowSpan: 2 },
            {
              label: `Sales Target ${targetYearTable}`,
              body: 'sales_target',
              colSpan: MONTH_TABLES.length,
            },
          ],
          MONTH_TABLES,
        ],
      },
    };

    const DEFAULT_STRUCTURE_BODY = [
      { label: 'Brand', body: 'brand' },
      ...MONTH_TABLES,
    ];
    const TABLE_BODY_STRUCTURES = {
      'sales-value': {
        national: DEFAULT_STRUCTURE_BODY,
        channel: {
          mt: DEFAULT_STRUCTURE_BODY,
          gt: DEFAULT_STRUCTURE_BODY,
          overall: DEFAULT_STRUCTURE_BODY,
        },
        branch: [
          { label: 'Branch', body: 'branch' },
          // {
          //   label: `Sales Target ${targetYearTable}`,
          //   body: 'sales_target',
          // },
          ...MONTH_TABLES,
        ],
        salesman: [
          { label: 'Salesman Code', body: 'salesman_code' },
          { label: 'Salesman Type', body: 'salesman_type' },
          // {
          //   label: `Sales Target ${targetYearTable}`,
          //   body: 'sales_target',
          // },
          ...MONTH_TABLES,
        ],
      },
      'product-focus': {
        salesman: [
          { label: 'Sales Type', body: 'salesman_type', rowSpan: 2 },
          ...MONTH_TABLES_WITHOUT_TOTAL,
        ],
      },
      doors: {
        national: [
          { label: 'Brand', body: 'brand', rowSpan: 2 },
          { label: 'SKU Reference', body: 'sku', rowSpan: 2 },
          ...MONTH_TABLES,
        ],
        salesman: [
          { label: 'Salesman Code', body: 'salesman_code', rowSpan: 2 },
          { label: 'Salesman Type', body: 'salesman_type', rowSpan: 2 },
          ...MONTH_TABLES,
        ],
      },
    };

    const targetType = paramTargetType.replace('-gt', '').replace('-mt', '');

    if (isSalesValueChannel) {
      const activeSubType = ['mt', 'gt', 'overall'].includes(activeTab)
        ? activeTab
        : 'mt';
      setTableHeader(
        TABLE_HEADER_STRUCTURES[paramType][targetType][activeSubType],
      );
      setTableStructures(
        TABLE_BODY_STRUCTURES[paramType][targetType][activeSubType],
      );
    } else {
      setTableHeader(TABLE_HEADER_STRUCTURES[paramType][targetType]);
      setTableStructures(TABLE_BODY_STRUCTURES[paramType][targetType]);
    }
    doSearch();
  };

  useEffect(() => {
    let mounted = true;
    if (mounted && !loading) {
      onChangeTab();
    }
    return () => {
      mounted = false;
    };
  }, [props.route.params, activeTab]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      const targetType = paramTargetType.replace('-gt', '').replace('-mt', '');
      if (targetType == 'channel') {
        setActiveTab('mt');
        setIsShowTab(true);
      } else {
        setActiveTab(targetType || 'national');
        setIsShowTab(false);
      }
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  return (
    <Layout.Container>
      {isWeb ? (
        <>
          <Layout.Title title={titlePage}>
            {showApproval ? (
              <View style={styles.submitWrapper}>
                <View style={styles.buttonWrapper}>
                  <Button
                    rounded
                    type="danger"
                    buttonStyle={styles.buttonStyle}
                    title={
                      <SociollaText type="medium" style={styles.buttonText}>
                        Reject
                      </SociollaText>
                    }
                    onPress={() => onSubmit('rejected')}
                  />
                </View>
                <View style={styles.buttonWrapper}>
                  <Button
                    rounded
                    buttonStyle={styles.buttonStyle}
                    title={
                      <SociollaText type="medium" style={styles.buttonText}>
                        Approve
                      </SociollaText>
                    }
                    onPress={() => onSubmit('approved')}
                  />
                </View>
              </View>
            ) : (
              false
            )}
          </Layout.Title>

          {props?.route?.params?.titleSection ? (
            <SociollaText style={styles.titleSubSection}>
              {props.route.params.titleSection}
            </SociollaText>
          ) : (
            false
          )}

          {isShowTab ? (
            <View style={styles.tabWrapper}>
              {TABS.map((item, idx) => (
                <TouchableOpacity
                  activeOpacity={0.8}
                  style={[
                    styles.tabItem,
                    activeTab == item.value ? styles.tabItemActive : {},
                  ]}
                  onPress={() => {
                    setActiveTab(item.value);
                  }}
                  key={idx}>
                  <SociollaText
                    type={activeTab == item.value ? 'medium' : 'regular'}
                    style={styles.tabItemText}>
                    {item.label}
                  </SociollaText>
                </TouchableOpacity>
              ))}
            </View>
          ) : (
            false
          )}

          <Layout.Wrapper fullWidth rounded>
            {loading ? (
              <Layout.Loader />
            ) : (
              <View style={styles.resultContainer}>
                {isProductFocus ? (
                  <>
                    {tableData.map((item, idx) => {
                      return (
                        <TableTargetSetting
                          key={idx}
                          index={idx}
                          prefixHeader={item?.name}
                          disabledEdit
                          targetSettingType={targetSettingType}
                          tableHeader={tableHeader}
                          tableStructures={tableStructures}
                          tableData={tableData}
                          setTableData={setTableData}
                        />
                      );
                    })}
                  </>
                ) : (
                  <TableTargetSetting
                    index={-1}
                    disabledEdit
                    targetSettingType={targetSettingType}
                    customSalesTargetHeader={customSalesTargetHeader}
                    tableHeader={tableHeader}
                    tableStructures={tableStructures}
                    tableData={tableData}
                    setTableData={setTableData}
                  />
                )}
              </View>
            )}
          </Layout.Wrapper>
        </>
      ) : (
        false
      )}
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  resultContainer: {
    width: '100%',
  },
  buttonWrapper: {
    marginLeft: 15,
  },
  buttonStyle: {
    width: 130,
  },
  buttonText: {
    fontSize: 14,
    color: Colors.white,
  },
  submitWrapper: {
    flexDirection: 'row',
  },
  tabWrapper: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    padding: 15,
    borderRadius: 5,
    backgroundColor: Colors.white,
    marginBottom: 20,
  },
  tabItem: {
    minWidth: 70,
    paddingVertical: 8,
    paddingHorizontal: 15,
    marginRight: 10,
    borderBottomColor: Colors.transparent,
    borderBottomWidth: 2,
  },
  tabItemActive: { borderBottomColor: Colors.blue0 },
  tabItemText: { textAlign: 'center' },
  titleSubSection: { fontSize: 16, marginBottom: 25 },
});

export default TargetSettingApprovalScreen;
